import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { io as socketIO } from 'socket.io-client';
import { useAuth } from '../context/AuthContext';

const DOC_TYPES = [
  { value: 'invoice', label: 'Invoice', icon: '📄' },
  { value: 'packing_list', label: 'Packing List', icon: '📋' },
  { value: 'contract', label: 'Contract', icon: '📝' },
  { value: 'shipping_doc', label: 'Shipping Doc', icon: '🚢' },
  { value: 'bill_of_lading', label: 'Bill of Lading', icon: '📦' },
  { value: 'other', label: 'Other', icon: '📎' },
];

const STATUS_COLORS = {
  pending: { bg: '#fef3c7', color: '#f59e0b' },
  approved: { bg: '#d1fae5', color: '#10b981' },
  rejected: { bg: '#fee2e2', color: '#ef4444' },
};

export default function OrderDetail() {
  const { orderId } = useParams();
  const { user, logout, isAdmin, isVendor } = useAuth();
  const [order, setOrder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const socketRef = useRef();
  const msgEndRef = useRef();

  const vendorPrefix = isVendor() ? '/api/vendor' : '/api';

  useEffect(() => {
    fetchAll();
    // Socket
    const token = localStorage.getItem('ts_token');
    socketRef.current = socketIO(window.location.origin, { auth: { token } });
    socketRef.current.on('connect', () => {
      socketRef.current.emit('join_order', orderId);
    });
    socketRef.current.on('new_message', (msg) => {
      setMessages(prev => [...prev, msg.message || msg]);
    });
    socketRef.current.on('new_document', () => fetchDocuments());
    socketRef.current.on('status_update', (data) => {
      setOrder(prev => prev ? { ...prev, status: data.status || data.new_status } : prev);
    });
    socketRef.current.on('document_status', ({ docId, status }) => {
      setDocuments(prev => prev.map(d => d.id === docId ? { ...d, status } : d));
    });
    return () => socketRef.current?.close();
  }, [orderId]);

  useEffect(() => {
    msgEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const ordersRes = await axios.get(`${vendorPrefix}/orders`);
      const allOrders = Array.isArray(ordersRes.data) ? ordersRes.data : (ordersRes.data.data || []);
      const o = allOrders.find(x => x.id === orderId);
      setOrder(o || null);
      await Promise.all([fetchDocuments(), fetchMessages()]);
    } catch (err) {
      console.error('Fetch error:', err);
    }
    setLoading(false);
  };

  const fetchDocuments = async () => {
    try {
      const res = await axios.get(`${vendorPrefix}/orders/${orderId}/documents`);
      setDocuments(Array.isArray(res.data) ? res.data : (res.data.data || []));
    } catch {}
  };

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${vendorPrefix}/orders/${orderId}/messages`);
      setMessages(Array.isArray(res.data) ? res.data : (res.data.data || []));
    } catch {}
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    try {
      await axios.post(`${vendorPrefix}/orders/${orderId}/messages`, { content: newMessage });
      setNewMessage('');
      await fetchMessages();
    } catch (err) {
      alert('Failed to send message');
    }
  };

  const handleFileUpload = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('doc_type', docType);
    try {
      await axios.post(`${vendorPrefix}/orders/${orderId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await fetchDocuments();
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.error || err.message));
    }
    setUploading(false);
    e.target.value = '';
  };

  const approveDoc = async (docId, status) => {
    try {
      await axios.patch(`/api/documents/${docId}/status`, { status });
      setDocuments(prev => prev.map(d => d.id === docId ? { ...d, status } : d));
    } catch {}
  };

  const updateStatus = async (status) => {
    try {
      await axios.patch(`${vendorPrefix}/orders/${orderId}/status`, { status });
      setOrder(prev => prev ? { ...prev, status } : prev);
    } catch {}
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading...</div>;
  if (!order) return <div style={{ padding: 40, textAlign: 'center' }}><div className="error-alert">Order not found</div><Link to="/dashboard">← Back</Link></div>;

  const getStatusBadgeClass = (status) => ({ draft: 'draft', confirmed: 'confirmed', production: 'production', shipped: 'shipped', delivered: 'delivered' }[status?.toLowerCase()] || 'draft');

  return (
    <div className="order-detail">
      <header className="header">
        <div className="header-content">
          <div className="header-left">
            <div className="header-logo">TradeSync</div>
            <nav className="header-nav">
              <Link to="/dashboard">Dashboard</Link>
              {isAdmin() && <Link to="/vendors">Vendors</Link>}
            </nav>
          </div>
          <div className="header-right">
            <div className="user-info"><span className="user-badge">{user?.role}</span><span>{user?.email}</span></div>
            <button className="logout-btn" onClick={logout}>Logout</button>
          </div>
        </div>
      </header>

      <main className="order-detail-content">
        <div className="order-detail-header">
          <div className="order-detail-header-left">
            <Link to="/dashboard" className="back-link">← Back to Dashboard</Link>
            <h2>Order #{order.order_number} <span className={`status-badge ${getStatusBadgeClass(order.status)}`} style={{ marginLeft: '1rem' }}>{order.status}</span></h2>
          </div>
          {isAdmin() && (
            <select className="status-select" value={order.status} onChange={(e) => updateStatus(e.target.value)}>
              {['draft', 'confirmed', 'production', 'shipped', 'delivered', 'cancelled'].map(s => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          )}
        </div>

        <div className="order-detail-grid">
          {/* Order Info */}
          <div className="order-info-card">
            <div className="order-info-header"><h3>📋 Order Information</h3></div>
            <div className="order-info-body">
              {[
                ['Vendor', order.vendor_name],
                ['Total Amount', formatCurrency(order.total_amount)],
                ['Port of Departure', order.port_departure || '—'],
                ['Port of Arrival', order.port_arrival || '—'],
                ['Vessel', order.vessel_name || '—'],
                ['Ship Date', order.ship_date || '—'],
                ['ETA', order.eta_date || '—'],
                ['Created', formatDate(order.created_at)],
                ['Last Updated', formatDate(order.updated_at)],
              ].map(([label, value]) => (
                <div key={label} className="info-row">
                  <span className="info-label">{label}</span>
                  <span className="info-value">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Documents */}
          <div className="documents-card">
            <div className="documents-header">
              <h3>📎 Documents</h3>
              <div className="upload-buttons">
                {DOC_TYPES.map(dt => (
                  <label key={dt.value} className="upload-btn" style={{ cursor: uploading ? 'wait' : 'pointer', opacity: uploading ? 0.5 : 1 }}>
                    {dt.icon} {dt.label}
                    <input type="file" style={{ display: 'none' }} onChange={(e) => handleFileUpload(e, dt.value)} accept=".pdf,.jpg,.jpeg,.png,.xlsx,.doc,.docx" />
                  </label>
                ))}
              </div>
            </div>
            <div className="documents-body">
              {documents.length === 0 ? (
                <div className="empty-state"><div className="empty-state-icon">📁</div><h3>No documents yet</h3></div>
              ) : (
                <div className="document-list">
                  {documents.map(doc => {
                    const sc = STATUS_COLORS[doc.status] || STATUS_COLORS.pending;
                    return (
                      <div key={doc.id} className="document-item">
                        <div className="document-header">
                          <div className="document-type">
                            <div className="document-icon">{DOC_TYPES.find(d => d.value === doc.doc_type)?.icon || '📎'}</div>
                            <div><div className="document-type-name">{(doc.doc_type || 'other').replace(/_/g, ' ')}</div></div>
                          </div>
                          <span className="document-version">v{doc.version}</span>
                        </div>
                        <div className="document-meta">
                          <span>By: {doc.uploaded_by}</span><span>•</span><span>{formatDate(doc.created_at)}</span><span>•</span>
                          <span style={{ background: sc.bg, color: sc.color, padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>{doc.status}</span>
                        </div>
                        <div className="document-actions">
                          <a href={`/api/documents/${doc.id}/download?token=${localStorage.getItem('ts_token')}`} className="doc-action-btn" style={{ textDecoration: 'none' }} target="_blank" rel="noopener">Download</a>
                          {isAdmin() && doc.status === 'pending' && (
                            <>
                              <button className="doc-action-btn approve" onClick={() => approveDoc(doc.id, 'approved')}>Approve</button>
                              <button className="doc-action-btn" style={{ color: '#ef4444' }} onClick={() => approveDoc(doc.id, 'rejected')}>Reject</button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Chat */}
          <div className="chat-card">
            <div className="chat-header"><h3>💬 Order Chat</h3></div>
            <div className="chat-messages">
              {messages.length === 0 ? (
                <div className="chat-empty"><p>No messages yet</p></div>
              ) : messages.map(msg => (
                <div key={msg.id} className={`chat-message ${msg.sender_type === (isVendor() ? 'vendor' : 'buyer') ? 'own' : 'other'}`}>
                  {(msg.sender_type === (isVendor() ? 'buyer' : 'vendor')) && <div className="message-sender">{msg.sender_name || msg.sender_type}</div>}
                  <div>{msg.content}</div>
                  <div className="message-time">{msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</div>
                </div>
              ))}
              <div ref={msgEndRef} />
            </div>
            <form className="chat-input-area" onSubmit={sendMessage}>
              <input className="chat-input" value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="Type your message..." />
              <button type="submit" className="chat-send-btn" disabled={!newMessage.trim()}>Send</button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
